import React, { useState, useEffect } from 'react';
import { clientApi, messageApi } from '../services/api';
import { Client } from '../types';
import ClientForm from './ClientForm';
import CsvUpload from './CsvUpload';
import { FaEdit, FaTrash, FaCar, FaBirthdayCake, FaPaperPlane, FaSpinner } from 'react-icons/fa';
import './ClientTable.css';

const ClientTable: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [sendingMessage, setSendingMessage] = useState<{ clientId: number; type: string } | null>(null);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async (): Promise<void> => {
    try {
      setLoading(true);
      const data = await clientApi.getAll();
      setClients(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar los clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number): Promise<void> => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este cliente?')) {
      return;
    }

    try {
      setDeletingId(id);
      await clientApi.delete(id);
      await loadClients();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Error al eliminar el cliente');
    } finally {
      setDeletingId(null);
    }
  };

  const handleEdit = (client: Client): void => {
    setEditingClient(client);
  };

  const handleSendTest = async (client: Client, type: 'rtm_reminder' | 'birthday'): Promise<void> => {
    if (!client.id) return;

    // Validar que el cliente tenga teléfono
    if (!client.phone) {
      alert('El cliente no tiene número de teléfono registrado');
      return;
    }

    // Validar que tenga la información necesaria según el tipo
    if (type === 'rtm_reminder' && !client.rtm_expiry_date) {
      if (!window.confirm('El cliente no tiene fecha de vencimiento RTM. ¿Deseas enviar el mensaje de todas formas?')) {
        return;
      }
    }

    if (type === 'birthday' && !client.birthday) {
      if (!window.confirm('El cliente no tiene fecha de cumpleaños registrada. ¿Deseas enviar el mensaje de todas formas?')) {
        return;
      }
    }

    try {
      setSendingMessage({ clientId: client.id, type });
      const result = await messageApi.sendTest(client.id, type);
      if (result.success) {
        alert(`✓ Mensaje ${type === 'rtm_reminder' ? 'de RTM' : 'de cumpleaños'} enviado exitosamente a ${client.name}`);
      } else {
        // Show the actual error message, not the formatted message
        const errorMsg = result.message || 'Error desconocido';
        alert(`✗ Error al enviar el mensaje: ${errorMsg}`);
      }
    } catch (err: any) {
      // Extract error message from response
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Error desconocido';
      alert(`✗ Error al enviar el mensaje: ${errorMsg}`);
    } finally {
      setSendingMessage(null);
    }
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES');
  };

  if (editingClient) {
    return (
      <div className="client-table-container">
        <ClientForm
          client={editingClient}
          onSuccess={() => {
            setEditingClient(null);
            loadClients();
          }}
          onCancel={() => setEditingClient(null)}
        />
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Cargando clientes...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="client-table-container">
      <CsvUpload onSuccess={loadClients} />
      {clients.length === 0 ? (
        <p>No hay clientes registrados</p>
      ) : (
        <table className="client-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Teléfono</th>
              <th>Email</th>
              <th>Ciudad</th>
              <th>Placa</th>
              <th>Tipo de Vehículo</th>
              <th>Marca</th>
              <th>Color</th>
              <th>Año</th>
              <th>Cilindraje</th>
              <th>Fecha RTM</th>
              <th>Vencimiento RTM</th>
              <th>Fecha de Cumpleaños</th>
              <th>Enviar Mensaje</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((client) => (
              <tr key={client.id}>
                <td>{client.name}</td>
                <td>{client.phone}</td>
                <td>{client.email || '-'}</td>
                <td>{client.city || '-'}</td>
                <td>{client.license_plate || '-'}</td>
                <td>
                  {client.vehicle_type === 'teaching' ? 'Enseñanza' :
                   client.vehicle_type === 'public' ? 'Público' :
                   client.vehicle_type === 'private' ? 'Particular' : '-'}
                </td>
                <td>{client.motorcycle_brand || '-'}</td>
                <td>{client.motorcycle_color || '-'}</td>
                <td>{client.motorcycle_year || '-'}</td>
                <td>{client.motorcycle_cc || '-'}</td>
                <td>{formatDate(client.rtm_date)}</td>
                <td>{formatDate(client.rtm_expiry_date)}</td>
                <td>{formatDate(client.birthday)}</td>
                <td className="send-message-column">
                  {client.phone ? (
                    <div className="send-buttons">
                      <button
                        onClick={() => handleSendTest(client, 'rtm_reminder')}
                        className="btn-send-instant btn-rtm"
                        disabled={sendingMessage?.clientId === client.id && sendingMessage?.type === 'rtm_reminder'}
                        title="Enviar recordatorio RTM ahora"
                      >
                        {sendingMessage?.clientId === client.id && sendingMessage?.type === 'rtm_reminder' ? (
                          <>
                            <FaSpinner className="spinning" /> Enviando...
                          </>
                        ) : (
                          <>
                            <FaCar /> RTM
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => handleSendTest(client, 'birthday')}
                        className="btn-send-instant btn-birthday"
                        disabled={sendingMessage?.clientId === client.id && sendingMessage?.type === 'birthday'}
                        title="Enviar felicitación de cumpleaños ahora"
                      >
                        {sendingMessage?.clientId === client.id && sendingMessage?.type === 'birthday' ? (
                          <>
                            <FaSpinner className="spinning" /> Enviando...
                          </>
                        ) : (
                          <>
                            <FaBirthdayCake /> Cumpleaños
                          </>
                        )}
                      </button>
                    </div>
                  ) : (
                    <span className="no-phone">Sin teléfono</span>
                  )}
                </td>
                <td className="actions">
                  <button
                    onClick={() => handleEdit(client)}
                    className="btn-edit"
                    title="Editar"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => client.id && handleDelete(client.id)}
                    className="btn-delete"
                    disabled={deletingId === client.id}
                    title="Eliminar"
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ClientTable;

